import {IDepartment, ISubDepartment} from '../../shared-models/stock/departments';

const VISIBLE_FIELD_DEP = 'dep';
const VISIBLE_FIELD_DEP_NAME = 'depName';
const VISIBLE_FIELD_SUB_DEP = 'subDep';
const VISIBLE_FIELD_SUB_DEP_NAME = 'subDepName';

let gridDepartments: IDepartment[] = [];

/**
 * Stores the current store's departments so grid valueGetters can resolve names.
 */
export function setGridDepartments(departments: IDepartment[] | undefined): void {
  gridDepartments = departments ?? [];
}

/**
 * Resolves a department code to its display name using the given or last loaded store departments.
 */
export function getDepartmentDisplayName(
  depCode: string | number | undefined | null,
  departments: IDepartment[] = gridDepartments,
): string {
  const code: string = String(depCode ?? '');
  if (!code || code === '0') {
    return '';
  }
  const departmentList: IDepartment[] = departments && departments.length > 0 ? departments : gridDepartments;
  const department: IDepartment | undefined = departmentList.find(
    (dep: IDepartment) => dep?.department === code,
  );
  return department?.name ?? code;
}

/**
 * Resolves a sub department code to its display name using the given or last loaded store departments.
 */
export function getSubDepartmentDisplayName(
  subDepCode: string | number | undefined | null,
  departments: IDepartment[] = gridDepartments,
): string {
  const code: string = String(subDepCode ?? '');
  if (!code || code === '0') {
    return '';
  }
  const departmentList: IDepartment[] = departments && departments.length > 0 ? departments : gridDepartments;
  const subDepartment: ISubDepartment | undefined = departmentList
    .flatMap((dep: IDepartment): ISubDepartment[] => Object.values(dep?.subDepartments ?? {}))
    .find((sub: ISubDepartment): boolean => sub?.subDepartment === code);
  return subDepartment?.name ?? code;
}

let gridDepartmentsByStore: Record<string, IDepartment[]> = {};

/**
 * Stores departments per store so multi-store grid rows resolve names from their own store.
 */
export function setGridDepartmentsByStore(departmentsByStore: Record<string, IDepartment[]> | undefined): void {
  gridDepartmentsByStore = departmentsByStore ?? {};
}

/**
 * Departments loaded for one store. Empty when that store has not loaded yet.
 */
export function getGridDepartmentsForStore(storeId: string | undefined | null): IDepartment[] {
  return storeId ? gridDepartmentsByStore[storeId] ?? [] : [];
}

/**
 * Department name from the row's own store. Shows the code until that store's departments load.
 */
export function getDepartmentDisplayNameForStore(
  storeId: string | undefined | null,
  depCode: string | number | undefined | null,
): string {
  const code: string = String(depCode ?? '');
  if (!code || code === '0') {
    return '';
  }
  const department: IDepartment | undefined = getGridDepartmentsForStore(storeId)
    .find((dep: IDepartment) => dep?.department === code);
  return department?.name ?? code;
}

/**
 * Sub department name from the row's own store. Shows the code until that store's departments load.
 */
export function getSubDepartmentDisplayNameForStore(
  storeId: string | undefined | null,
  subDepCode: string | number | undefined | null,
): string {
  const code: string = String(subDepCode ?? '');
  if (!code || code === '0') {
    return '';
  }
  const subDepartment: ISubDepartment | undefined = getGridDepartmentsForStore(storeId)
    .flatMap((dep: IDepartment): ISubDepartment[] => Object.values(dep?.subDepartments ?? {}))
    .find((sub: ISubDepartment): boolean => sub?.subDepartment === code);
  return subDepartment?.name ?? code;
}

/**
 * Inserts a name field directly after its code field when the saved list has the code but not the name,
 * so an existing selection keeps showing what the old combined column showed.
 */
function ensureNameAlongsideCode(fields: string[], codeField: string, nameField: string, legacyCodeField: string,
                                 legacyNameField: string): string[] {
  if (!Array.isArray(fields) || fields.length === 0) {
    return fields ?? [];
  }
  if (fields.includes(nameField) || fields.includes(legacyNameField)) {
    return fields;
  }
  const nextFields: string[] = [...fields];
  const codeIndex: number = nextFields.indexOf(codeField);
  if (codeIndex >= 0) {
    nextFields.splice(codeIndex + 1, 0, nameField);
    return nextFields;
  }
  const legacyCodeIndex: number = nextFields.indexOf(legacyCodeField);
  if (legacyCodeIndex >= 0) {
    nextFields.splice(legacyCodeIndex + 1, 0, legacyNameField);
  }
  return nextFields;
}

/**
 * Saved visible-field lists that still have Department but not Department Name
 * get the name column next to the code, matching the old combined column.
 */
export function ensureDepNameAlongsideDep(fields: string[]): string[] {
  return ensureNameAlongsideCode(fields, VISIBLE_FIELD_DEP, VISIBLE_FIELD_DEP_NAME, 'department', 'department_name');
}

/**
 * Saved visible-field lists that still have Sub Department but not Sub Department Name
 * get the name column next to the code, matching the old combined column.
 */
export function ensureSubDepNameAlongsideSubDep(fields: string[]): string[] {
  return ensureNameAlongsideCode(
    fields,
    VISIBLE_FIELD_SUB_DEP,
    VISIBLE_FIELD_SUB_DEP_NAME,
    'sub_department',
    'sub_department_name',
  );
}

/**
 * Applies both the Department Name and Sub Department Name migrations to a saved visible-field list.
 */
export function ensureDepartmentNameFields(fields: string[]): string[] {
  return ensureSubDepNameAlongsideSubDep(ensureDepNameAlongsideDep(fields));
}

const PREVIEW_FIELD_TO_GRID_FIELD = new Map<string, string>([
  ['department', VISIBLE_FIELD_DEP],
  ['department_name', VISIBLE_FIELD_DEP_NAME],
  ['sub_department', VISIBLE_FIELD_SUB_DEP],
  ['sub_department_name', VISIBLE_FIELD_SUB_DEP_NAME],
]);

/**
 * Maps a saved preview/visible-field id onto the grid column field it controls.
 */
export function toGridVisibleField(field: string): string {
  return PREVIEW_FIELD_TO_GRID_FIELD.get(field) ?? field;
}
